import { pipe } from 'fp-ts/function';
import { compact, isEmpty, map } from 'lodash/fp';
import { useContext } from 'react';
import { Field } from 'redux-form';
import { LocalizedOptions } from '@mews-commander/types';
import { FieldSet } from '@mews-commander/ui';
import { Stack, WithChildren } from '@optimus-web/core';
import { LanguageContext } from '../Localization';
import LanguageLabel from '../Localization/LanguageLabel';

interface Props extends WithChildren {
    label: React.ReactNode;
    name: string;
    disabled?: boolean;
    requiredFields?: string[];
    localizedOptions?: LocalizedOptions;
}

const createLocalizedField =
    <P,>(component: React.ComponentType<P>): React.ComponentType<Props> =>
    ({ name, label, disabled, requiredFields, localizedOptions, ...props }) => {
        const selectedLanguageCodes = useContext(LanguageContext);

        if (isEmpty(selectedLanguageCodes)) {
            return null;
        }

        return (
            <FieldSet label={label} disabled={disabled} style={{ marginTop: 24, marginBottom: 0 }}>
                <Stack vertical spacing="100">
                    {pipe(
                        selectedLanguageCodes,
                        compact,
                        map((langCode) => (
                            <Field
                                key={langCode}
                                name={`${name}.${langCode}`}
                                component={component}
                                props={{
                                    label: <LanguageLabel languageCode={langCode} />,
                                    disabled,
                                    required: requiredFields && requiredFields.includes(langCode),
                                    options: localizedOptions && localizedOptions[langCode],
                                    ...props,
                                }}
                            />
                        ))
                    )}
                </Stack>
            </FieldSet>
        );
    };

export default createLocalizedField;
